<template>
    <div class="document-segment-mapper">
      <div class="mapping-panel">
        <div class="mapping-header" @click="toggleMappingPanel">
          <h3 class="mapping-title">Assign Images to Segments</h3>
          <button class="toggle-btn" :class="{ 'is-collapsed': !mappingPanelExpanded }">
            <span class="toggle-icon"></span>
          </button>
        </div>

        <div v-show="mappingPanelExpanded" class="mapping-content">
          <div class="instructions">
            <p>Assign images to specific segments of your conversation
              <ProFeatureChip :is-active="isPro" />
            </p>
          </div>

          <div class="segment-list">
            <div
              v-for="(segment, index) in segments"
              :key="`segment-${index}`"
              class="segment-item"
              :class="{ 'active': selectedSegmentIndex === index }"
              @click="selectSegment(index)"
            >
              <div class="segment-info">
                <span class="segment-number">{{ index + 1 }}</span>
                <span class="segment-speaker">{{ segment.speaker }}</span>
                <span class="segment-preview">{{ truncateText(segment.content, 60) }}</span>
              </div>
              <div class="document-badges">
                <span
                  v-if="getDocumentsForSegment(index).length > 0"
                  class="document-counter"
                >
                  {{ getDocumentsForSegment(index).length }}
                </span>
              </div>
            </div>
          </div>

          <!-- Document Carousel for Selected Segment -->
          <div v-if="selectedSegmentIndex !== null" class="document-selector">
            <div class="selector-header">
            <h4>Documents for Segment {{ selectedSegmentIndex + 1 }}</h4>
            </div>

            <!-- Carousel Navigation Arrows -->
            <div class="carousel-container">
              <div class="scroll-arrow left" @click.stop="scrollCarousel('left')" v-if="showScrollButtons && hasScrollLeft">
                <span>◀</span>
              </div>

              <!-- Carousel Track -->
              <div class="carousel-track" ref="carouselTrack" @scroll="handleScroll">
                <div
                  v-for="doc in allDocuments"
                  :key="doc.uniqueKey"
                  class="carousel-item"
                  :class="{ 'mapped': isDocumentMappedToSegment(doc.uniqueKey, selectedSegmentIndex) }"
                  @click="toggleDocumentForSegment(doc.uniqueKey, selectedSegmentIndex)"
                >
                  <div class="upload-card">
                    <div class="upload-image">
                      <img v-if="isImageFile(doc.type)"
                          :src="doc.dataUrl || doc.preview"
                          :alt="doc.name"
                          @error="handleImageError">
                      <div v-else class="file-icon" :class="getFileIconClass(doc.type)">
                        {{ getFileExtension(doc.name).toUpperCase() }}
                      </div>
                    </div>
                    <div class="upload-info">
                      <div class="upload-title">{{ truncateFileName(doc.name) }}</div>
                      <div class="upload-meta">
                        {{ formatFileSize(doc.size) }} · {{ formatFileType(doc.type) }}
                      </div>
                    </div>
                    <div class="mapping-indicator" :class="{ 'is-mapped': isDocumentMappedToSegment(doc.uniqueKey, selectedSegmentIndex) }">
                      {{ isDocumentMappedToSegment(doc.uniqueKey, selectedSegmentIndex) ? '✓' : '+' }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="scroll-arrow right" @click.stop="scrollCarousel('right')" v-if="showScrollButtons && hasScrollRight">
                <span>▶</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Preview Modal -->
      <div v-if="previewOpen" class="preview-modal" @click="closePreview">
        <div class="preview-content" @click.stop>
          <div class="preview-header">
            <h3>{{ activeDoc ? activeDoc.name : '' }}</h3>
            <button class="close-preview" @click="closePreview">×</button>
          </div>
          <div class="preview-body">
            <img v-if="activeDoc && isImageFile(activeDoc.type)"
                 :src="activeDoc.dataUrl || activeDoc.preview"
                 :alt="activeDoc.name">
            <div v-else class="preview-placeholder">
              <div class="file-icon large" :class="getFileIconClass(activeDoc ? activeDoc.type : '')">
                {{ getFileExtension(activeDoc ? activeDoc.name : '').toUpperCase() }}
              </div>
              <p>Preview not available</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <script>
  import API from '@/api';
  import ProFeatureChip from '../components/ProFeatureChip.vue';
  import { mapActions } from 'vuex';
  export default {
    name: 'DocumentSegmentMapper',
    components: {
      ProFeatureChip
    },
    props: {
      isMobile: {
        type: Boolean,
        default: false
      },
      isPro: {
        type: Boolean,
        default: false
      },
      messageId: {
        required: true
      },
      segments: {
        type: Array,
        required: true,
        default: () => []
      },
      activeSegmentIndex: {
        type: Number,
        default: null
      },
      lastDocumentUpdate: {
        type: Number,
        default: 0
      },
      uploadedDocuments: {
        type: Object,
        default: () => ({})
      },
      documentData: {
      type: Object,
      default: () => ({})
    },
      showMappingPanel: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        mappingPanelExpanded: true,
        selectedSegmentIndex: null,
        documentSegmentMap: {}, // Format: { segmentIndex: ['uniqueKey1', 'uniqueKey2', ...] }
        // Carousel data
        showScrollButtons: true,
        hasScrollLeft: false,
        hasScrollRight: true,
        // Preview data
        previewOpen: false,
        activeDoc: null
      };
    },
    computed: {
      documentMapKey() {
        return `doc-segment-map-${this.messageId}`;
      },
      allDocuments() {
        // Build a combined collection from both sources with documentData taking precedence
        const documents = [];
        const processedKeys = new Set(); // Track which document keys we've already processed

        // First process documentData (priority source)
        if (this.documentData) {
        Object.keys(this.documentData).forEach(messageId => {
            const messageDocs = this.documentData[messageId];

            Object.keys(messageDocs).forEach(docKey => {
            // Add unique key and message ID if not present
            const docWithId = {
                ...messageDocs[docKey],
                uniqueKey: docKey,
                messageId
            };

            documents.push(docWithId);
            processedKeys.add(docKey); // Mark this key as processed
            });
        });
        }

        // Then supplement with uploadedDocuments for any keys we haven't seen yet
        Object.keys(this.uploadedDocuments || {}).forEach(messageId => {
        const messageDocs = this.uploadedDocuments[messageId];

        Object.keys(messageDocs).forEach(docKey => {
            // Only add if we haven't processed this key yet
            if (!processedKeys.has(docKey)) {
            documents.push({
                ...messageDocs[docKey],
                uniqueKey: docKey,
                messageId
            });
            processedKeys.add(docKey);
            }
        });
        });

        return documents;
    }
    },
    watch: {
      lastDocumentUpdate: {
        handler(newVal, oldVal) {
        if (newVal !== oldVal) {
            // // Force computed properties to re-evaluate
            this.$forceUpdate();
            // Refresh the document display
            this.$nextTick(() => {
            if (this.$refs.carouselTrack) {
                this.handleScroll();
            }
            });
        }
      }
    },
      activeSegmentIndex(newVal, oldVal) {
        if (newVal !== null && newVal !== oldVal) {
          // Check if this segment has documents
          const hasDocuments = this.getDocumentsForSegment(newVal).length > 0;

          // If it has documents, emit an event
          if (hasDocuments) {
            this.$emit('segment-has-documents', {
              segmentIndex: newVal,
              documentCount: this.getDocumentsForSegment(newVal).length,
              documents: this.getDocumentsForSegment(newVal)
            });
          }
        }
      },
      selectedSegmentIndex() {
        // Reset scroll position when selected segment changes
        this.$nextTick(() => {
          if (this.$refs.carouselTrack) {
            this.$refs.carouselTrack.scrollLeft = 0;
            this.handleScroll();
          }
        });
      }
    },
    methods: {
      ...mapActions('websocket', ['showTempMessageFn']),
      refreshDocumentData() {
        // Force computed properties to re-evaluate
        this.$forceUpdate();
        // Refresh the document display
        this.$nextTick(() => {
            if (this.$refs.carouselTrack) {
            this.handleScroll();
            }
        });
      },
      toggleMappingPanel() {
        this.mappingPanelExpanded = !this.mappingPanelExpanded;
      },

      selectSegment(index) {
        if (!this.isPro) {
          this.showTempMessageFn({
            message: 'Customization is only available for Pro users.',
            type: 'warning'
          });
          return;
        }
        this.selectedSegmentIndex = this.selectedSegmentIndex === index ? null : index;
        this.$emit('segment-selected', this.selectedSegmentIndex);
      },

      getDocumentsForSegment(segmentIndex) {
        if (!this.documentSegmentMap[segmentIndex]) {
          return [];
        }

        // Get the document keys for this segment
        const docKeys = this.documentSegmentMap[segmentIndex];

        // Map the keys to actual document objects
        return docKeys.map(key => {
          // Extract messageId from uniqueKey (assuming format: messageId_uploadId)
          const [messageId] = key.split('_');

          // Find the document in the uploaded documents
          const allDocs = this.uploadedDocuments[messageId] || {};
          const doc = allDocs[key];

          // Ensure the document has a uniqueKey property
          if (doc && !doc.uniqueKey) {
            doc.uniqueKey = key;
          }

          return doc;
        }).filter(doc => doc); // Filter out any undefined documents (if a document was removed)
      },

      isDocumentMappedToSegment(docKey, segmentIndex) {
        return this.documentSegmentMap[segmentIndex] &&
               this.documentSegmentMap[segmentIndex].includes(docKey);
      },

      async toggleDocumentForSegment(docKey, segmentIndex) {
        // Initialize the array for this segment if it doesn't exist
        if (!this.documentSegmentMap[segmentIndex]) {
          this.$set(this.documentSegmentMap, segmentIndex, []);
        }

        const index = this.documentSegmentMap[segmentIndex].indexOf(docKey);
        const document = this.allDocuments.find(doc => doc.uniqueKey === docKey);

        if (!document) {
          console.error('Document not found:', docKey);
          return;
        }

        if (index === -1) {
          // Add the document to this segment
          this.documentSegmentMap[segmentIndex].push(docKey);
        } else {
          // Remove the document from this segment
          this.documentSegmentMap[segmentIndex].splice(index, 1);
        }

        try {
          // Save the mapping to the backend.
          await API.post(`/user_customization/messages/${this.messageId}/segments/${segmentIndex}/mappings`, {
            document_ids: this.documentSegmentMap[segmentIndex].map(key => {
              // Find the document by uniqueKey and get its ID
              const doc = this.allDocuments.find(d => d.uniqueKey === key);
              return doc ? doc.id : null;
            }).filter(id => id !== null)
          });

          // Emit an event with the updated mapping
          this.$emit('mapping-updated', {
            segmentIndex,
            documentKeys: this.documentSegmentMap[segmentIndex],
            messageId: this.messageId,
            documentWithData: document
          });
        } catch (error) {
          console.error('Error saving segment mapping:', error);
        }
      },

      // Carousel methods
      scrollCarousel(direction) {
        if (!this.$refs.carouselTrack) return;

        const scrollAmount = 300; // Adjust as needed
        const currentScroll = this.$refs.carouselTrack.scrollLeft;

        if (direction === 'left') {
          this.$refs.carouselTrack.scrollLeft = currentScroll - scrollAmount;
        } else {
          this.$refs.carouselTrack.scrollLeft = currentScroll + scrollAmount;
        }
      },

      handleScroll() {
        if (!this.$refs.carouselTrack) return;

        const track = this.$refs.carouselTrack;

        // Check if we can scroll left or right
        this.hasScrollLeft = track.scrollLeft > 0;
        this.hasScrollRight = track.scrollLeft < (track.scrollWidth - track.clientWidth);
      },

      // Preview methods
      openPreview(doc) {
        this.activeDoc = doc;
        this.previewOpen = true;

        // Prevent body scrolling
        document.body.style.overflow = 'hidden';
      },

      closePreview() {
        this.previewOpen = false;
        this.activeDoc = null;

        // Restore body scrolling
        document.body.style.overflow = '';
      },

      // Helper methods
      truncateText(text, maxLength) {
        if (!text) return '';

        // First strip HTML tags
        const plainText = text.replace(/<[^>]+>/g, '');

        if (plainText.length <= maxLength) return plainText;
        return plainText.substring(0, maxLength) + '...';
      },

      truncateFileName(name) {
        if (!name) return '';
        return name.length > 20 ? name.substring(0, 17) + '...' : name;
      },

      formatFileSize(size) {
        if (!size) return '';
        if (size < 1024) return size + ' B';
        if (size < 1024 * 1024) return Math.round(size / 1024) + ' KB';
        return Math.round(size / (1024 * 1024) * 10) / 10 + ' MB';
      },

      formatFileType(type) {
        if (!type) return 'File';
        return type.split('/').pop().toUpperCase();
      },

      isImageFile(fileType) {
        return fileType && fileType.startsWith('image/');
      },

      getFileExtension(filename) {
        if (!filename) return '';
        return filename.split('.').pop() || '';
      },

      getFileIconClass(fileType) {
        if (!fileType) return 'generic-icon';

        if (fileType.includes('pdf')) return 'pdf-icon';
        if (fileType.includes('word') || fileType.includes('doc')) return 'doc-icon';
        if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return 'xls-icon';
        if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) return 'ppt-icon';

        return 'generic-icon';
      },

      handleImageError(e) {
        e.target.style.display = 'none';
        const placeholder = document.createElement('div');
        placeholder.className = 'file-icon generic-icon';
        placeholder.textContent = this.getFileExtension(e.target.alt).toUpperCase();
        e.target.parentNode.appendChild(placeholder);
      }
    },
    mounted() {

      this.$nextTick(() => {
        if (this.$refs.carouselTrack) {
          this.handleScroll();
        }
      });
    }
  }
  </script>

  <style scoped>
  .document-segment-mapper {
    width: 100%;
    margin-bottom: 1rem;
  }

  /* Mapping Panel Styles */
  .mapping-panel {
    background: rgba(4, 255, 134, 0.05);
    border: 1px solid rgba(4, 255, 134, 0.15);
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 1rem;
  }

  .mapping-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    cursor: pointer;
    background: #e53e3e;
  }

  .mapping-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 500;
    color: white;
  }

  .toggle-btn {
    background: none;
    border: none;
    width: 24px;
    height: 24px;
    position: relative;
    cursor: pointer;
  }

  .toggle-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border-right: 2px solid rgba(4, 255, 134, 0.8);
    border-bottom: 2px solid rgba(4, 255, 134, 0.8);
    transform-origin: center;
    transition: transform 0.2s ease;
    transform: translate(-50%, -50%) rotate(45deg);
  }

  .toggle-btn.is-collapsed .toggle-icon {
    transform: translate(-50%, -50%) rotate(-135deg);
  }

  .mapping-content {
    padding: 1rem;
  }

  .instructions {
    margin-bottom: 1rem;
    font-size: 0.85rem;
    opacity: 0.8;
  }

  /* Segment List Styles */
  .segment-list {
    max-height: 250px;
    overflow-y: auto;
    border: 1px solid rgba(4, 255, 134, 0.2);
    border-radius: 6px;
    margin-bottom: 1rem;
  }

  .segment-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid rgba(4, 255, 134, 0.1);
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .segment-item:last-child {
    border-bottom: none;
  }

  .segment-item:hover {
    background: rgba(4, 255, 134, 0.08);
  }

  .segment-item.active {
    background: rgba(4, 255, 134, 0.15);
  }

  .segment-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
  }

  .segment-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: rgba(4, 255, 134, 0.2);
    color: rgba(4, 255, 134, 0.9);
    border-radius: 50%;
    font-size: 0.8rem;
    font-weight: bold;
  }

  .segment-speaker {
    font-weight: 500;
    font-size: 0.85rem;
  }

  .segment-preview {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }

  .document-badges {
    display: flex;
    gap: 0.25rem;
  }

  .document-counter {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    background: rgba(4, 255, 134, 0.25);
    color: rgba(4, 255, 134, 1);
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: bold;
  }

  /* Document Selector Styles */
  .document-selector {
    margin-top: 1.5rem;
  }

  .document-selector h4 {
    margin-top: 0;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(4, 255, 134, 0.9);
  }

  /* Carousel Styles */
  .carousel-container {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0;
    cursor: pointer;
  }

  .carousel-track {
    display: flex;
    overflow-x: auto;
    scroll-behavior: smooth;
    padding: 0.5rem;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    gap: 16px;
  }

  .carousel-track::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  .carousel-item {
    flex: 0 0 auto;
    width: 220px;
    height: 180px;
    border-radius: 8px;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  .carousel-item.mapped::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #00ff00 0%, #ff00ff 33%, #00ffff 66%, #ff00ff 100%);
    background-size: 200% 100%;
    animation: gradientSlide 1.5s linear infinite;
  }

  .upload-card {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: rgba(4, 255, 134, 0.07);
    border: 1px solid rgba(4, 255, 134, 0.15);
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.2s ease;
    position: relative;
  }

  .carousel-item.mapped .upload-card {
    border-color: rgba(4, 255, 134, 0.5);
    background: rgba(4, 255, 134, 0.12);
  }

  .upload-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
    border-color: rgba(4, 255, 134, 0.3);
    background: rgba(4, 255, 134, 0.12);
  }

  .upload-image {
    height: 65%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.1);
  }

  .upload-image img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }

  .upload-card:hover .upload-image img {
    transform: scale(1.05);
  }

  .upload-info {
    padding: 10px;
    height: 35%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .upload-title {
    font-weight: 500;
    font-size: 0.85rem;
    line-height: 1.2;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin-bottom: 4px;
  }

  .upload-meta {
    font-size: 0.7rem;
    color: gray;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Mapping indicator */
  .mapping-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(4, 255, 134, 0.2);
    color: white;
    font-size: 16px;
    font-weight: bold;
    transition: all 0.2s ease;
  }

  .mapping-indicator.is-mapped {
    background: rgba(4, 255, 134, 0.8);
  }

  /* Scroll Arrows */
  .scroll-arrow {
    position: absolute;
    width: 40px;
    height: 40px;
    background: rgba(4, 255, 134, 0.15);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: all 0.2s ease;
  }

  .scroll-arrow:hover {
    background: rgba(4, 255, 134, 0.3);
  }

  .scroll-arrow.left {
    left: 10px;
  }

  .scroll-arrow.right {
    right: 10px;
  }

  .scroll-arrow span {
    color: white;
    font-size: 0.9rem;
  }

  /* Preview Modal */
  .preview-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .preview-content {
    width: 95%;
    height: 95%;
    max-width: 1600px;
    max-height: 95vh;
    background: #222;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .preview-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: rgba(4, 255, 134, 0.1);
    border-bottom: 1px solid rgba(4, 255, 134, 0.2);
  }

  .preview-header h3 {
    margin: 0;
    font-size: 1.1rem;
    color: white;
    font-weight: 500;
  }

  .close-preview {
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
  }

  .preview-body {
    flex: 1;
    overflow: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: #111;
  }

  .preview-body img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .preview-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 20px;
  }

  .preview-placeholder p {
    color: #aaa;
    font-size: 1.1rem;
  }

  /* File Icon Styles */
  .file-icon {
    width: 80px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1.2rem;
    color: white;
    position: relative;
  }

  .file-icon:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #e0e0e0;
    clip-path: polygon(0% 0%, 80% 0%, 100% 20%, 100% 100%, 0% 100%);
  }

  .file-icon:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 20px;
    height: 20px;
    background: #c0c0c0;
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
  }

  .file-icon.large {
    width: 160px;
    height: 200px;
    font-size: 2rem;
  }

  .pdf-icon:before { background: #FF5722; }
  .doc-icon:before { background: #2196F3; }
  .xls-icon:before { background: #4CAF50; }
  .ppt-icon:before { background: #FF9800; }
  .generic-icon:before { background: #9E9E9E; }

  /* Animations */
  @keyframes gradientSlide {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* Mobile Responsive Design */
  @media screen and (max-width: 768px) {
    .carousel-item {
      width: 180px;
      height: 160px;
    }

    .scroll-arrow {
      width: 32px;
      height: 32px;
    }

    .upload-title {
      font-size: 0.8rem;
    }
  }

  .selector-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .selector-header h4 {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(4, 255, 134, 0.9);
  }

  .refresh-btn {
    background: rgba(4, 255, 134, 0.15);
    border: 1px solid rgba(4, 255, 134, 0.25);
    border-radius: 4px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .refresh-btn:hover {
    background: rgba(4, 255, 134, 0.25);
    transform: scale(1.05);
  }

  .refresh-btn:active {
    transform: scale(0.95);
  }

  .refresh-icon {
    font-size: 16px;
    color: rgba(4, 255, 134, 0.9);
    transition: transform 0.3s ease;
  }

  .refresh-btn:hover .refresh-icon {
    transform: rotate(180deg);
  }
  </style>
